import { useEffect, useState } from "react";
import { useGetIdentity, useNotify, useRedirect } from "ra-core";
import { ClipboardList, Plus, Search, Edit2, Trash2, ExternalLink } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { getSupabaseClient } from "../providers/supabase/supabase";

type PO = {
  id: number;
  po_number: string | null;
  supplier_id: number | null;
  sales_order_id: number | null;
  status: string;
  currency: string;
  total_amount: number | null;
  deposit_paid: number | null;
  balance_paid: number | null;
  factory_delivery_date: string | null;
  payment_terms: string | null;
  notes: string | null;
  created_at: string;
  suppliers?: { name: string } | null;
  orders?: { order_number: string } | null;
};

const STATUS_BADGE: Record<string, string> = {
  draft: "bg-gray-100 text-gray-600",
  confirmed: "bg-blue-100 text-blue-700",
  in_production: "bg-purple-100 text-purple-700",
  qc_passed: "bg-yellow-100 text-yellow-700",
  shipped: "bg-green-100 text-green-700",
  closed: "bg-slate-100 text-slate-500",
};

const STATUS_LABELS: Record<string, string> = {
  draft: "Draft",
  confirmed: "Confirmed",
  in_production: "In Production",
  qc_passed: "QC Passed",
  shipped: "Shipped",
  closed: "Closed",
};

type Supplier = { id: number; name: string };
type Order = { id: number; order_number: string };

const emptyForm = () => ({
  supplier_id: "",
  sales_order_id: "",
  status: "draft",
  currency: "USD",
  total_amount: "",
  deposit_paid: "",
  balance_paid: "",
  factory_delivery_date: "",
  payment_terms: "",
  notes: "",
});

const PurchaseOrdersPage = () => {
  const { identity } = useGetIdentity();
  const notify = useNotify();
  const redirect = useRedirect();
  const [pos, setPos] = useState<PO[]>([]);
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<PO | null>(null);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);

  const load = () => {
    const supabase = getSupabaseClient();
    Promise.all([
      supabase.from("purchase_orders")
        .select("*, suppliers(name), orders!purchase_orders_sales_order_id_fkey(order_number)")
        .order("created_at", { ascending: false }),
      supabase.from("suppliers").select("id, name").eq("status", "active").order("name"),
      supabase.from("orders").select("id, order_number").order("created_at", { ascending: false }).limit(100),
    ]).then(([posRes, suppRes, ordRes]) => {
      setPos(posRes.data as PO[] ?? []);
      setSuppliers(suppRes.data ?? []);
      setOrders(ordRes.data ?? []);
      setLoading(false);
    });
  };

  useEffect(() => { load(); }, []);

  const set = (k: string, v: string) => setForm(f => ({ ...f, [k]: v }));

  const openCreate = () => { setEditing(null); setForm(emptyForm()); setShowForm(true); };
  const openEdit = (po: PO) => {
    setEditing(po);
    setForm({
      supplier_id: po.supplier_id?.toString() ?? "",
      sales_order_id: po.sales_order_id?.toString() ?? "",
      status: po.status,
      currency: po.currency,
      total_amount: po.total_amount?.toString() ?? "",
      deposit_paid: po.deposit_paid?.toString() ?? "",
      balance_paid: po.balance_paid?.toString() ?? "",
      factory_delivery_date: po.factory_delivery_date ?? "",
      payment_terms: po.payment_terms ?? "",
      notes: po.notes ?? "",
    });
    setShowForm(true);
  };

  const handleSave = async () => {
    setSaving(true);
    const supabase = getSupabaseClient();
    const payload = {
      supplier_id: form.supplier_id ? parseInt(form.supplier_id) : null,
      sales_order_id: form.sales_order_id ? parseInt(form.sales_order_id) : null,
      status: form.status,
      currency: form.currency,
      total_amount: form.total_amount ? parseFloat(form.total_amount) : null,
      deposit_paid: form.deposit_paid ? parseFloat(form.deposit_paid) : 0,
      balance_paid: form.balance_paid ? parseFloat(form.balance_paid) : 0,
      factory_delivery_date: form.factory_delivery_date || null,
      payment_terms: form.payment_terms || null,
      notes: form.notes || null,
      updated_at: new Date().toISOString(),
    };
    if (editing) {
      const { error } = await supabase.from("purchase_orders").update(payload).eq("id", editing.id);
      if (error) { notify(error.message, { type: "error" }); setSaving(false); return; }
      notify("Purchase order updated", { type: "info" });
    } else {
      const { error } = await supabase.from("purchase_orders").insert({ ...payload, created_by: identity?.id ?? null });
      if (error) { notify(error.message, { type: "error" }); setSaving(false); return; }
      notify("Purchase order created", { type: "info" });
    }
    setSaving(false); setShowForm(false); load();
  };

  const handleDelete = async (id: number) => {
    if (!confirm("Delete this purchase order?")) return;
    await getSupabaseClient().from("purchase_orders").delete().eq("id", id);
    notify("Deleted", { type: "info" }); load();
  };

  const filtered = pos.filter(po => {
    if (statusFilter !== "all" && po.status !== statusFilter) return false;
    if (search) {
      const q = search.toLowerCase();
      return (po.po_number ?? "").toLowerCase().includes(q) ||
        (po.suppliers?.name ?? "").toLowerCase().includes(q) ||
        (po.orders?.order_number ?? "").toLowerCase().includes(q);
    }
    return true;
  });

  return (
    <div className="p-6 space-y-5 max-w-6xl mx-auto">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold flex items-center gap-2"><ClipboardList className="w-6 h-6" /> Purchase Orders</h1>
        <Button size="sm" onClick={openCreate} className="gap-1"><Plus className="w-3.5 h-3.5" /> New PO</Button>
      </div>

      <div className="flex gap-3 items-center">
        <div className="relative flex-1 max-w-xs">
          <Search className="absolute left-2.5 top-2 w-3.5 h-3.5 text-muted-foreground" />
          <Input className="pl-8 h-8 text-sm" placeholder="PO#, supplier, order..." value={search} onChange={e => setSearch(e.target.value)} />
        </div>
        <div className="flex border rounded-lg overflow-hidden">
          {["all", "draft", "confirmed", "in_production", "qc_passed", "shipped", "closed"].map(s => (
            <button key={s} onClick={() => setStatusFilter(s)}
              className={`px-2.5 py-1.5 text-xs font-medium capitalize transition-colors ${statusFilter === s ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}>
              {s === "all" ? "All" : STATUS_LABELS[s] ?? s}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="space-y-2">{[1,2,3].map(i => <div key={i} className="h-14 bg-muted animate-pulse rounded-lg" />)}</div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16 text-muted-foreground">
          <ClipboardList className="w-10 h-10 mx-auto mb-3 opacity-30" />
          <p>No purchase orders. <button onClick={openCreate} className="text-primary underline">Create the first PO</button></p>
        </div>
      ) : (
        <div className="bg-card border rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-muted/50 text-xs text-muted-foreground uppercase">
                <th className="text-left px-4 py-2.5">PO #</th>
                <th className="text-left px-4 py-2.5">Supplier</th>
                <th className="text-left px-4 py-2.5 hidden md:table-cell">Sales Order</th>
                <th className="text-right px-4 py-2.5">Total</th>
                <th className="text-right px-4 py-2.5 hidden lg:table-cell">ETD</th>
                <th className="text-center px-4 py-2.5">Status</th>
                <th className="px-4 py-2.5 w-20"></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(po => (
                <tr key={po.id} className="border-t hover:bg-muted/20 transition-colors">
                  <td className="px-4 py-3 font-mono text-xs font-medium">{po.po_number ?? `PO-${po.id}`}</td>
                  <td className="px-4 py-3">
                    {po.suppliers?.name ?? <span className="text-muted-foreground">—</span>}
                  </td>
                  <td className="px-4 py-3 hidden md:table-cell">
                    {po.orders?.order_number ? (
                      <button onClick={() => redirect("show", "orders", po.sales_order_id!)}
                        className="text-primary hover:underline flex items-center gap-1 text-xs">
                        {po.orders.order_number} <ExternalLink className="w-3 h-3" />
                      </button>
                    ) : <span className="text-muted-foreground">—</span>}
                  </td>
                  <td className="px-4 py-3 text-right tabular-nums">
                    {po.total_amount != null ? `${po.currency} ${po.total_amount.toLocaleString()}` : "—"}
                  </td>
                  <td className="px-4 py-3 text-right hidden lg:table-cell text-muted-foreground">
                    {po.factory_delivery_date ? new Date(po.factory_delivery_date).toLocaleDateString() : "—"}
                  </td>
                  <td className="px-4 py-3 text-center">
                    <span className={`text-xs px-2 py-0.5 rounded-full ${STATUS_BADGE[po.status] ?? ""}`}>
                      {STATUS_LABELS[po.status] ?? po.status}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex gap-1 justify-end">
                      <button onClick={() => openEdit(po)} className="p-1 text-muted-foreground hover:text-foreground"><Edit2 className="w-3.5 h-3.5" /></button>
                      <button onClick={() => handleDelete(po.id)} className="p-1 text-muted-foreground hover:text-destructive"><Trash2 className="w-3.5 h-3.5" /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={showForm} onOpenChange={o => !o && setShowForm(false)}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing ? "Edit Purchase Order" : "New Purchase Order"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2 space-y-1.5">
              <Label>Supplier</Label>
              <Select value={form.supplier_id} onValueChange={v => set("supplier_id", v)}>
                <SelectTrigger className="h-8"><SelectValue placeholder="Select supplier..." /></SelectTrigger>
                <SelectContent>
                  {suppliers.map(s => <SelectItem key={s.id} value={s.id.toString()}>{s.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-2 space-y-1.5">
              <Label>Linked Sales Order</Label>
              <Select value={form.sales_order_id} onValueChange={v => set("sales_order_id", v)}>
                <SelectTrigger className="h-8"><SelectValue placeholder="Link to order..." /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="">None</SelectItem>
                  {orders.map(o => <SelectItem key={o.id} value={o.id.toString()}>{o.order_number}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Status</Label>
              <Select value={form.status} onValueChange={v => set("status", v)}>
                <SelectTrigger className="h-8"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(STATUS_LABELS).map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Currency</Label>
              <Select value={form.currency} onValueChange={v => set("currency", v)}>
                <SelectTrigger className="h-8"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {["USD","EUR","CNY","GBP"].map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Total Amount</Label>
              <Input className="h-8 text-sm" type="number" step="0.01" value={form.total_amount} onChange={e => set("total_amount", e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label>Factory ETD</Label>
              <Input className="h-8 text-sm" type="date" value={form.factory_delivery_date} onChange={e => set("factory_delivery_date", e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label>Deposit Paid</Label>
              <Input className="h-8 text-sm" type="number" step="0.01" value={form.deposit_paid} onChange={e => set("deposit_paid", e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label>Balance Paid</Label>
              <Input className="h-8 text-sm" type="number" step="0.01" value={form.balance_paid} onChange={e => set("balance_paid", e.target.value)} />
            </div>
            <div className="col-span-2 space-y-1.5">
              <Label>Payment Terms</Label>
              <Input className="h-8 text-sm" placeholder="30% deposit, 70% before shipment" value={form.payment_terms} onChange={e => set("payment_terms", e.target.value)} />
            </div>
            <div className="col-span-2 space-y-1.5">
              <Label>Notes</Label>
              <Textarea className="text-sm resize-none" rows={2} value={form.notes} onChange={e => set("notes", e.target.value)} />
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" size="sm" onClick={() => setShowForm(false)}>Cancel</Button>
            <Button size="sm" onClick={handleSave} disabled={saving}>{saving ? "Saving..." : (editing ? "Update" : "Create PO")}</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PurchaseOrdersPage;
